import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { EnvBadge } from '@/deploy/components/ui/EnvBadge';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { ConsoleModal } from '@/deploy/components/ui/ConsoleModal';
import { useSessionStore } from '@/deploy/stores/session.store';
import { betaSlotService, type BetaSlot } from '@/deploy/services/beta-slot.service';
import { devEnvService, type DevEnv } from '@/deploy/services/dev-env.service';
import { serverControlService } from '@/deploy/services/server-control.service';
import { can } from '@/deploy/utils/permissions';
import { formatRelative } from '@/deploy/utils/format';
import { CopyField, fmtUptime } from './DevEnvDetail';
import { PromoteFromDevModal } from './Environments';
import styles from './DevEnvDetail.module.scss';

export function BetaSlotDetail() {
  const { slotId } = useParams<{ slotId: string }>();
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());
  const user = useSessionStore((s) => s.user);
  const canPromote = can(user, 'env.beta.promote');

  const [slot, setSlot] = useState<BetaSlot | null>(null);
  const [devEnvs, setDevEnvs] = useState<DevEnv[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [stats, setStats] = useState<{ ramMb: number; cpuPct: number; uptimeSec: number } | null>(null);

  const refetch = useCallback(
    () => betaSlotService.list(project.id).then((l) => l.find((s) => s.slot === slotId) ?? null),
    [project.id, slotId],
  );
  // Dev envs : sert UNIQUEMENT à la modale de promotion → chargement NON bloquant et
  // tolérant à l'échec (ne doit pas empêcher l'affichage du slot ni de sa console).
  const loadDevEnvs = useCallback(() => {
    devEnvService.list(project.id).then(setDevEnvs).catch(() => { /* non essentiel */ });
  }, [project.id]);

  useEffect(() => {
    if (!project.id || !slotId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    refetch()
      .then((s) => { if (!cancelled) setSlot(s); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Erreur'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    loadDevEnvs();
    return () => { cancelled = true; };
  }, [refetch, loadDevEnvs, project.id, slotId]);

  const configured = !!slot?.configured;

  // Poll ressources (RAM / CPU / uptime) pour un slot configuré (serveur Ptero fixe).
  useEffect(() => {
    if (!configured || !project.id || !slotId) { setStats(null); return; }
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      try {
        const s = await serverControlService.state(project.id, 'beta', slotId);
        if (!alive) return;
        setStats({
          ramMb: Math.round((s.memoryBytes ?? 0) / (1024 * 1024)),
          cpuPct: Math.round((s.cpuAbsolute ?? 0) * 10) / 10,
          uptimeSec: Math.round((s.uptimeMs ?? 0) / 1000),
        });
      } catch { /* garde l'ancienne valeur sur erreur transitoire */ }
      finally { if (alive) timer = setTimeout(tick, 3000); }
    };
    void tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [configured, project.id, slotId]);

  if (loading) {
    return <div className={styles.page}><div className={styles.empty}>Chargement du slot…</div></div>;
  }
  if (error || !slot) {
    return (
      <div className={styles.notFound}>
        <h2>Slot introuvable</h2>
        {error && <p style={{ color: 'var(--color-text-muted)' }}>{error}</p>}
        <Button variant="primary" onClick={() => navigate('/deploiement/environments/beta')}>Retour aux slots Bêta</Button>
      </div>
    );
  }

  const addr = slot.publicAddress ?? '';
  const sep = addr.lastIndexOf(':');
  const ip = sep > 0 ? addr.slice(0, sep) : addr;
  const port = sep > 0 ? addr.slice(sep + 1) : '';
  const occupied = !!slot.currentDevEnvId;

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => navigate('/deploiement/environments/beta')}>
        <Icon name="chevron-right" size={14} style={{ transform: 'rotate(180deg)' }} /> Tous les slots Bêta
      </button>

      <PageHeader
        eyebrow={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <EnvBadge env="beta" size="sm" />
            {occupied
              ? <>Poussé {slot.lastPromotedAt ? formatRelative(slot.lastPromotedAt) : ''}{slot.lastPromotedBy ? <> par {slot.lastPromotedBy}</> : null}</>
              : 'Slot libre'}
          </span>
        }
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className={styles.chip} style={{ background: '#38BDF8', fontFamily: 'var(--font-family-mono)', fontSize: 22, fontWeight: 700 }}>
              {slot.slot}
            </span>
            {slot.currentDevEnvDisplay ?? `Slot ${slot.slot}`}
            {!configured
              ? <Badge tone="muted">Non configuré</Badge>
              : occupied ? <Badge tone="warning" dot>Occupé</Badge> : <Badge tone="success" dot>Libre</Badge>}
          </span>
        }
        subtitle={`Slot Bêta ${slot.slot}${slot.pteroUuid ? ` · Ptero ${slot.pteroUuid}` : ''}`}
        actions={
          configured && canPromote ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="primary" size="md" leftIcon={<Icon name="arrow-right" size={14} />}
                disabled={promoting || clearing} loading={promoting}
                onClick={() => { loadDevEnvs(); setPromoteOpen(true); }}>
                {occupied ? 'Remplacer' : 'Pousser un dev env'}
              </Button>
              {occupied && (
                <Button variant="ghost" size="md" leftIcon={<Icon name="x" size={14} />}
                  disabled={promoting || clearing} loading={clearing}
                  onClick={() => {
                    if (confirm(`Vider le slot Bêta ${slot.slot} ?\nLe serveur beta sera arrêté et ses fichiers effacés, le slot repassera « libre ».`)) {
                      setClearing(true);
                      betaSlotService.clear(project.id, slot.slot)
                        .then(() => refetch().then(setSlot).catch(() => { /* refetch KO : l'op a réussi, ne pas inverser le verdict */ }))
                        .catch((e) => alert(`Vidage du slot ${slot.slot} a échoué : ${e instanceof Error ? e.message : String(e)}`))
                        .finally(() => setClearing(false));
                    }
                  }}>
                  Vider le slot
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      {/* Infos rapides */}
      <section className={styles.metaRow}>
        <CopyField label="Adresse IP" value={slot.publicAddress ? ip : null} empty="non alloué" />
        <CopyField label="Port" value={port || null} />
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Env source</span>
          <code className={styles.metaValue}>{slot.currentDevEnvDisplay ?? '—'}</code>
        </div>
        {stats && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Ressources</span>
            <div className={styles.resourceRow}>
              <div className={styles.resourceMetric}>
                <span className={styles.resourceLabel}>Uptime</span>
                <span className={styles.resourceValue}>{stats.uptimeSec > 0 ? fmtUptime(stats.uptimeSec) : '—'}</span>
              </div>
              <div className={styles.resourceMetric}>
                <span className={styles.resourceLabel}>RAM</span>
                <span className={styles.resourceValue}>{stats.ramMb} MB</span>
              </div>
              <div className={styles.resourceMetric}>
                <span className={styles.resourceLabel}>CPU</span>
                <span className={styles.resourceValue}>{stats.cpuPct}%</span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Console live + explorateur de fichiers (drag & drop) — panneau intégré */}
      {configured ? (
        <ConsoleModal
          embedded
          open
          onClose={() => { /* embedded : pas de fermeture */ }}
          projectId={project.id}
          scope="beta"
          targetKey={slot.slot}
          sourceName={`Bêta ${slot.slot}`}
          sourceSubtitle={slot.currentDevEnvDisplay ?? `slot ${slot.slot}`}
          icon="server"
          // Identifier Pterodactyl = les 8 premiers caractères de l'UUID ; les slots sont sur le Pterodactyl de dev
          crashTarget={slot.pteroUuid ? { env: 'dev', identifier: slot.pteroUuid.slice(0, 8) } : undefined}
        />
      ) : (
        <div className={styles.empty}>
          Slot non configuré — renseigne l'UUID Ptero de ce slot dans Settings → Config Ptero pour accéder à la console et aux fichiers.
        </div>
      )}

      {promoteOpen && (
        <PromoteFromDevModal
          slot={slot}
          devEnvs={devEnvs.filter((e) => e.status === 'running' || e.status === 'stopped')}
          onClose={() => setPromoteOpen(false)}
          onLaunch={(devEnvId) => {
            setPromoteOpen(false);
            setPromoting(true);
            betaSlotService.promote(project.id, slot.slot, devEnvId)
              .then(() => refetch().then(setSlot).catch(() => { /* refetch KO : l'op a réussi */ }))
              .catch((e) => alert(`Pousser vers Bêta ${slot.slot} a échoué : ${e instanceof Error ? e.message : String(e)}`))
              .finally(() => { setPromoting(false); loadDevEnvs(); });
          }}
        />
      )}
    </div>
  );
}
